import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InspectionService } from 'src/app/services/inspections/inspection.service';
import { InspectionModalService } from 'src/app/services/inspection-modal/inspection-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-md-inspection',
  templateUrl: './add-edit-md-inspection.component.html',
  styleUrls: ['./add-edit-md-inspection.component.scss']
})
export class AddEditMdInspectionComponent extends DestructorComponent implements OnInit {

  @Output() inspectionAdded = new EventEmitter<void>();
  @Output() inspectionUpdated = new EventEmitter<void>();

  inspectionForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedInspection: any = null;

  constructor(
    private fb: FormBuilder,
    private _inspectionService: InspectionService,
    private _inspectionModalService: InspectionModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.inspectionForm = this.fb.group({
      caption: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      status: [1, [Validators.required]],
      display_order: ['', [Validators.required, Validators.min(0)]]
    });
  }

  // Getter methods for form controls
  get caption() { return this.inspectionForm.get('caption'); }
  get description() { return this.inspectionForm.get('description'); }
  get status() { return this.inspectionForm.get('status'); }
  get display_order() { return this.inspectionForm.get('display_order'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_inspection_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceInspection = this._inspectionModalService.getSelectedInspection();
        if (serviceInspection) {
          this.isEditMode = true;
          this.selectedInspection = serviceInspection;
          this.populateForm(this.selectedInspection);
        } else {
          this.isEditMode = false;
          this.selectedInspection = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._inspectionModalService.clearSelectedInspection();
        this.resetForm();
      });
    }
  }

  private populateForm(inspection: any): void {
    this.inspectionForm.patchValue({
      caption: inspection.caption || '',
      description: inspection.description || '',
      status: inspection.status !== undefined ? inspection.status : 1,
      display_order: inspection.display_order || ''
    });
  }

  private resetForm(): void {
    this.inspectionForm.reset();
    this.inspectionForm.patchValue({
      status: 1 // Default to active
    });
    this.isLoading = false;
    this.markFormGroupUntouched(this.inspectionForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_inspection_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.inspectionForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.inspectionForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._inspectionService.UpdateData(this.selectedInspection._id, submitData)
        : this._inspectionService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Inspection operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Inspection has been successfully updated!"
                : "Inspection has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.inspectionUpdated.emit();
                } else {
                  this.inspectionAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Inspection operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.inspectionForm);
    }
  }
}
